import React from 'react';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';
import { useSelector } from 'react-redux';
import {
  checkCustomPackage,
  checkFreePackage,
  formatNumberWithSuffix,
  getCustomPackagePrice,
} from '@src/services/package';
import { PACKAGE_LEVEL } from '@src/constants/package';
import AmountCredits from '@src/containers/NewPricingModelV3/AmountCredits';
import { getOptimalVouchersForPackage } from '@src/services/voucher';
import { checkVietNam } from '@src/utils/checkCountry';
import Text from '../Shared/Text';
import { getCreditsWithBestVoucher } from '../Credits/CreditsUsed';
import CheckOrCross from '../Shared/CheckOrCross';

const UsageLimitFeature = ({ pkg, canUseVouchers, totalVoucher }) => {
  const bestVoucher = canUseVouchers(pkg)?.[0];
  const isVietNam = checkVietNam();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const optionsSelected = useSelector(
    (state) => state.packages.optionsSelected,
  );

  const isCustomPackage = checkCustomPackage({
    type: pkg.type,
    level: pkg.level,
  });

  if (isCustomPackage) {
    const { price: priceVipPlusPackagePrice, vndPrice } = getCustomPackagePrice(
      {
        credits: optionsSelected.credits,
        voiceClonings: optionsSelected.voiceClonings,
        devices: optionsSelected.devices,
        concurrentRequestApi: optionsSelected.concurrentRequestApi,
        customResource: pkg?.customResource,
        isVietNam,
      },
    );

    const [bestVoucherCustomPkg] = getOptimalVouchersForPackage({
      pkg: {
        ...pkg,
        price: priceVipPlusPackagePrice,
        vndPrice,
        usdPrice: priceVipPlusPackagePrice,
        custom: { credits: optionsSelected.credits },
      },
      vouchers: totalVoucher,
    });
    const usageLimitWithBestVoucher = getCreditsWithBestVoucher({
      bestVoucher: bestVoucherCustomPkg,
      credits: optionsSelected?.credits,
      level: PACKAGE_LEVEL.CUSTOM,
      totalCycles: 1,
    });
    const creditsFormatted = formatNumberWithSuffix(
      usageLimitWithBestVoucher,
      language,
    );

    return (
      <Text
        key={pkg.code}
        html={
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('creditsUsedPerYear', {
                  credits: creditsFormatted,
                }),
                {
                  allowedTags: ['span', 'strong'],
                  allowedAttributes: {
                    span: ['style'],
                  },
                },
              ),
            }}
          />
        }
      />
    );
  }

  return (
    <div className="flex justify-center">
      <AmountCredits
        pkg={pkg}
        bestVoucher={bestVoucher}
        hideDot
        usageLimit
        newPricingOverviewEnabled
      />
    </div>
  );
};

const usageLimitFeatures = ({ packages, canUseVouchers, totalVoucher }) =>
  packages.map((pkg) =>
    checkFreePackage(pkg?.code) ? (
      <CheckOrCross key={pkg.code} isCheck={false} />
    ) : (
      <UsageLimitFeature
        key={pkg.code}
        pkg={pkg}
        canUseVouchers={canUseVouchers}
        totalVoucher={totalVoucher}
      />
    ),
  );

export default usageLimitFeatures;
